/**
 * PatternLibrarySection.jsx — GRANULLO_CANONICAL_PATTERN_LIBRARY_AND_TRANSACTION_ALLOCATION_ENGINE_V1
 *
 * Universal transaction allocation infrastructure.
 * Intent → Conditions → Allocation → Proof → Release.
 *
 * LEFT:  Pattern Library (8 primitives). Click = selects the financial law.
 * RIGHT: Live simulation panel. Shows the allocation, proof, release, and
 *        execution rail (rail-agnostic banking partner) that the chosen pattern produces.
 *
 * This is not a product page. It is a doctrine surface.
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, Shield, CheckCircle2, Lock,
  Landmark, Building2, HeartHandshake, Store, Crown, Sun, Recycle, KeyRound,
  MousePointerClick, Cpu, Sparkles, Loader2, FileCheck2, AlertCircle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SCARLET = '#CD2200';
const EMERALD = '#10B981';

/* ═══════════════════════ PATTERN PRIMITIVES ═══════════════════════ */
const PATTERNS = [
  {
    id: 'vat-gst',
    domain: 'Government',
    name: 'VAT / GST Protected',
    flagship: true,
    flagshipNote: 'Pattern of choice for tax authorities',
    synopsis: 'Tax obligations reserved structurally — before the supplier ever sees the funds.',
    icon: Landmark,
    tag: '5% reserved at source',
    intent: 'Pay this supplier for AED 10,000 invoice.',
    conditions: [
      'invoice.totalAmount == 10000',
      'supplier.isRegistered == true',
      'jurisdiction.vatRate == 0.05',
    ],
    allocation: [
      { label: 'Supplier',     amount: 'AED 9,523.81', pct: 95.24, tone: 'primary' },
      { label: 'VAT Reserve',  amount: 'AED 476.19',   pct: 4.76,  tone: 'reserved', note: 'separated pre-release' },
    ],
    proof: [
      'VAT reserve set aside before funds touch supplier.',
      'Supplier receives compliant amount only.',
      'Authority remittance reconciled at the same tick — no quarter-close scramble.',
    ],
    release: 'Executed via banking rail · BL+COO+TAX evidence frozen in twin',
    twinRisk: 'Tax leakage / withholding failure',
    twinFix: 'VAT automatically reserved before release',
    twinOutcome: 'Money did not move incorrectly.',
  },
  {
    id: 'procurement-withholding',
    domain: 'Enterprise',
    name: 'Procurement + Withholding',
    synopsis: 'Vendor payments release only after obligations are structurally separated.',
    icon: Building2,
    tag: '10% withheld at source',
    intent: 'Pay vendor $80,000 for services rendered.',
    conditions: [
      'vendor.isOnboarded == true',
      'withholding.rate == 0.10',
      'services.delivered == true',
    ],
    allocation: [
      { label: 'Vendor',              amount: '$72,000',  pct: 90,  tone: 'primary' },
      { label: 'Withholding Reserve', amount: '$8,000',   pct: 10,  tone: 'reserved', note: 'remitted to authority' },
    ],
    proof: [
      'Withholding separated before vendor payout.',
      'Zero manual reconciliation downstream.',
      'Authority receives its share at the same tick.',
    ],
    release: 'Executed via banking rail · withholding certificate auto-issued',
    twinRisk: 'Withholding miscalculation · late remittance',
    twinFix: '10% withheld at source, remitted in the same transaction',
    twinOutcome: 'Obligation met before opportunity to fail.',
  },
  {
    id: 'foundation-release',
    domain: 'Foundation',
    name: 'Program Release',
    synopsis: 'Scholarships, grants, and aid release only when proof exists.',
    icon: HeartHandshake,
    tag: 'Proof-gated release',
    intent: 'Release $4,200 scholarship tranche.',
    conditions: [
      'student.biometric.verified == true',
      'institution.accredited == true',
      'term.attendance >= 0.85',
    ],
    allocation: [
      { label: 'Tuition (institution)', amount: '$3,600',  pct: 85.71, tone: 'primary' },
      { label: 'Stipend (student)',     amount: '$600',    pct: 14.29, tone: 'secondary' },
    ],
    proof: [
      'Biometric attendance verified at institution.',
      'Institution accreditation checked at release-time.',
      'No proof → no release. Structural.',
    ],
    release: 'Executed via banking rail · proof hash sealed to twin',
    twinRisk: 'Ghost-student fraud · diversion',
    twinFix: 'Attendance + accreditation verified before funds move',
    twinOutcome: 'Scholarship arrived only where proof existed.',
  },
  {
    id: 'marketplace-split',
    domain: 'Platforms',
    name: 'Marketplace Split',
    synopsis: 'Commissions and vendor payouts structurally allocated before settlement.',
    icon: Store,
    tag: '3-way atomic split',
    intent: 'Settle ₹50,000 marketplace order.',
    conditions: [
      'order.status == "delivered"',
      'seller.kyc.verified == true',
      'platformCommission.rate == 0.08',
    ],
    allocation: [
      { label: 'Seller',             amount: '₹44,000',   pct: 88, tone: 'primary' },
      { label: 'Platform commission', amount: '₹4,000',    pct: 8,  tone: 'secondary' },
      { label: 'GST reserve',         amount: '₹2,000',    pct: 4,  tone: 'reserved' },
    ],
    proof: [
      'Commission + GST split before seller receives anything.',
      'Seller sees net amount from day one.',
      'Platform never holds money it cannot prove.',
    ],
    release: 'Executed via banking rail · 3-way allocation in one movement',
    twinRisk: 'Commission disputes · mis-posted GST',
    twinFix: 'Three-way split computed and released atomically',
    twinOutcome: 'No reconciliation. No disputes.',
  },
  {
    id: 'royalty-lock',
    domain: 'Franchise',
    name: 'Royalty Lock',
    synopsis: 'Franchise fees and royalties never depend on manual reconciliation.',
    icon: Crown,
    tag: 'POS-verified royalty',
    intent: 'Franchisee remits $24,000 weekly GMV.',
    conditions: [
      'pos.verified == true',
      'royalty.rate == 0.065',
      'marketingFund.rate == 0.015',
    ],
    allocation: [
      { label: 'Franchisee retains', amount: '$21,840',  pct: 91, tone: 'primary' },
      { label: 'Royalty (franchisor)', amount: '$1,560',  pct: 6.5, tone: 'secondary' },
      { label: 'Marketing fund',     amount: '$360',      pct: 1.5, tone: 'secondary' },
      { label: 'Tax reserve',        amount: '$240',      pct: 1,   tone: 'reserved' },
    ],
    proof: [
      'Royalty + marketing fund split at POS settlement.',
      'Franchisor sees true revenue, not reported revenue.',
      'No trust required between brand and franchisee.',
    ],
    release: 'Executed via banking rail · POS-verified allocation',
    twinRisk: 'Royalty under-reporting',
    twinFix: 'Royalty deducted at source from verified POS',
    twinOutcome: 'Royalty owed and royalty received are the same number.',
  },
  {
    id: 'solar-yield',
    domain: 'Climate Finance',
    name: 'Solar Yield & Revenue Share',
    synopsis: 'Energy revenue splits automatically across operator, investor, farmer.',
    icon: Sun,
    tag: 'Meter-signed yield',
    intent: 'Distribute $12,000 monthly solar yield.',
    conditions: [
      'meter.kwh.signed == true',
      'operator.share == 0.35',
      'investor.share == 0.50',
      'landowner.share == 0.15',
    ],
    allocation: [
      { label: 'Investor',  amount: '$6,000',  pct: 50, tone: 'primary' },
      { label: 'Operator',  amount: '$4,200',  pct: 35, tone: 'secondary' },
      { label: 'Landowner', amount: '$1,800',  pct: 15, tone: 'secondary' },
    ],
    proof: [
      'Meter reading verified on-chain before payout.',
      'All three parties paid in the same tick.',
      'No operator discretion over split.',
    ],
    release: 'Executed via banking rail · kWh hash sealed to twin',
    twinRisk: 'Yield mis-allocation · investor mistrust',
    twinFix: 'Meter-verified, cryptographically signed split',
    twinOutcome: 'Climate capital actually compounds for every party.',
  },
  {
    id: 'circularity-cbdc',
    domain: 'Circularity',
    name: 'Credit + CBDC Distribution',
    synopsis: 'Circularity Credit revenue allocated across recycler, community, CBDC beneficiaries.',
    icon: Recycle,
    tag: 'Programmable CBDC',
    intent: 'Distribute $3,000 circularity credit proceeds.',
    conditions: [
      'credit.verified == true',
      'recycler.share == 0.60',
      'community.share == 0.25',
      'program.share == 0.15',
    ],
    allocation: [
      { label: 'Recycler',          amount: '$1,800', pct: 60, tone: 'primary' },
      { label: 'Community fund',    amount: '$750',   pct: 25, tone: 'secondary' },
      { label: 'CBDC beneficiary',  amount: '$450',   pct: 15, tone: 'secondary' },
    ],
    proof: [
      'Credit retirement verified before distribution.',
      'Community fund receives its share atomically.',
      'CBDC beneficiary is proof-eligible.',
    ],
    release: 'Executed via banking rail → programmable CBDC channel',
    twinRisk: 'Greenwashing · diverted community share',
    twinFix: 'Retired credit + proof-of-eligibility precede release',
    twinOutcome: 'Circularity capital arrives at the last mile, verified.',
  },
  {
    id: 'escrow-milestone',
    domain: 'Treasury',
    name: 'Escrow + Milestone Release',
    synopsis: 'Capital moves only when delivery conditions are true.',
    icon: KeyRound,
    tag: 'Milestone-gated capital',
    intent: 'Release $250,000 milestone 2 payment.',
    conditions: [
      'milestone.deliverable.signed == true',
      'qa.passed == true',
      'buyer.approval == "explicit"',
    ],
    allocation: [
      { label: 'Contractor',  amount: '$237,500',  pct: 95, tone: 'primary' },
      { label: 'Retainage',   amount: '$12,500',   pct: 5,  tone: 'reserved', note: 'released on final milestone' },
    ],
    proof: [
      'Deliverable signed off and QA verified.',
      'Retainage held structurally, not contractually.',
      'Buyer approval is cryptographic, not email.',
    ],
    release: 'Executed via banking rail · milestone proof sealed',
    twinRisk: 'Premature release · retainage disputes',
    twinFix: 'Milestone proof precedes every cent moved',
    twinOutcome: 'Contract risk collapsed to zero.',
  },
];

const USED_ACROSS = [
  'Government', 'CSR', 'Treasury', 'Procurement', 'Platforms',
  'Solar', 'Payroll', 'Trade Finance', 'Escrow', 'Climate Finance',
];

export default function PatternLibrarySection() {
  const [activeId, setActiveId] = useState(PATTERNS[0].id);
  const p = PATTERNS.find(x => x.id === activeId) || PATTERNS[0];

  // ── Proof → Release lifecycle (real, transactional, MongoDB-backed) ──
  const [lifecycleState, setLifecycleState] = useState('idle'); // idle | running | done | error
  const [lifecycleResult, setLifecycleResult] = useState(null);
  const [lifecycleError, setLifecycleError] = useState(null);

  React.useEffect(() => {
    // Reset lifecycle output when pattern changes
    setLifecycleState('idle');
    setLifecycleResult(null);
    setLifecycleError(null);
  }, [activeId]);

  const runLifecycle = async () => {
    setLifecycleState('running');
    setLifecycleError(null);
    setLifecycleResult(null);
    try {
      const resp = await fetch(`${API}/api/proof-lifecycle/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pattern_id: p.id,
          pattern_name: p.name,
          intent: p.intent,
          conditions: p.conditions || [],
          allocation: (p.allocation || []).map(a => ({
            label: a.label, amount: a.amount, pct: a.pct, tone: a.tone, note: a.note,
          })),
          proof: p.proof || [],
          release: p.release,
          actor: 'public',
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setLifecycleResult(data);
      setLifecycleState('done');
    } catch (err) {
      setLifecycleError(err.message || String(err));
      setLifecycleState('error');
    }
  };

  return (
    <section
      id="pattern-library"
      className="relative"
      style={{ background: 'var(--shell-bg)', padding: '90px 0 90px' }}
      data-testid="pattern-library-section"
    >
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8">

        {/* ═══ HEADER: Category-defining manifesto ═══ */}
        <div className="mb-14 sm:mb-16 relative">
          {/* Eyebrow badge — scarlet command */}
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full"
            style={{
              background: `${SCARLET}0A`,
              border: `1px solid ${SCARLET}40`,
              boxShadow: `0 0 18px ${SCARLET}14`,
            }}
          >
            <motion.span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: SCARLET, boxShadow: `0 0 6px ${SCARLET}` }}
              animate={{ opacity: [1, 0.35, 1] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
            />
            <span className="text-[10px] font-mono uppercase tracking-[0.24em] font-semibold" style={{ color: SCARLET }}>
              Universal Transaction Allocation
            </span>
            <span className="text-[10px] font-mono uppercase tracking-[0.16em]" style={{ color: `${SCARLET}AA` }}>
              · Pattern Library v1
            </span>
          </motion.div>

          {/* Supertitle — doctrine primitive before the big claim */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ delay: 0.1 }}
            className="text-[11px] font-mono uppercase tracking-[0.3em] mb-4"
            style={{ color: 'var(--shell-text-muted)' }}
          >
            Intent → Conditions → Allocation → Proof → Release
          </motion.p>

          {/* Main headline — category-defining claim */}
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
            className="text-[42px] sm:text-[62px] lg:text-[80px] font-semibold leading-[0.96] tracking-[-0.035em] mb-5 max-w-[1020px]"
            style={{ color: 'var(--shell-text)', fontFamily: '"Source Serif 4", Georgia, serif' }}
            data-testid="pattern-library-heading"
          >
            Choose the kind of truth
            <br />
            <span style={{ color: SCARLET }}>you want enforced.</span>
          </motion.h2>

          {/* Handwritten weapon line */}
          <motion.p
            initial={{ opacity: 0, rotate: -4 }}
            whileInView={{ opacity: 1, rotate: -2 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ delay: 0.35, duration: 0.6 }}
            className="inline-block mb-6"
            style={{
              fontFamily: '"Kalam", cursive',
              fontSize: 28,
              color: SCARLET,
              lineHeight: 1,
              letterSpacing: '0.01em',
            }}
          >
            not a rule that complains — a rule that prevents.
          </motion.p>

          {/* Sub-paragraph */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ delay: 0.2 }}
            className="text-[15px] sm:text-[17px] leading-[1.6] max-w-[680px] mb-10"
            style={{ color: 'var(--shell-text-secondary)' }}
          >
            Pick a pattern. Granullo compiles it into executable certainty —
            obligations separated, conditions verified, proof attached
            <strong style={{ color: 'var(--shell-text)' }}> before money moves</strong>.
            See exactly what would have released — and what would not.
          </motion.p>

          {/* Stat ribbon — scale-at-a-glance */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-0 mb-12 rounded-xl overflow-hidden"
            style={{
              background: 'var(--shell-surface)',
              border: '1px solid var(--shell-border)',
              boxShadow: '0 1px 0 rgba(255,255,255,0.5) inset',
            }}
            data-testid="pattern-stat-ribbon"
          >
            {[
              { big: '8',      label: 'transaction primitives' },
              { big: '∞',      label: 'executable outcomes',  serif: true },
              { big: '0',      label: 'trust required' },
              { big: 'Any Rail', label: 'execution-partner agnostic', mono: true },
            ].map((s, i) => (
              <div key={i} className="relative px-5 py-4"
                style={{ borderRight: i < 3 ? '1px solid var(--shell-border)' : 'none' }}
              >
                <p
                  className="text-[28px] sm:text-[32px] font-semibold leading-none tracking-[-0.02em] mb-1 tabular-nums"
                  style={{
                    color: 'var(--shell-text)',
                    fontFamily: s.serif ? '"Source Serif 4", Georgia, serif' : (s.mono ? '"SF Mono", ui-monospace, monospace' : 'inherit'),
                    fontSize: s.mono ? '20px' : undefined,
                  }}
                >
                  {s.big}
                </p>
                <p className="text-[10px] font-mono uppercase tracking-[0.18em]" style={{ color: 'var(--shell-text-muted)' }}>
                  {s.label}
                </p>
              </div>
            ))}
          </motion.div>

          {/* ═══ 3-STEP FLOW — icon-led, connected with arrows ═══ */}
          <p className="text-[10px] font-mono uppercase tracking-[0.22em] mb-4" style={{ color: 'var(--shell-text-muted)' }}>
            How it works · 3 moves
          </p>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ delay: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] gap-3 md:gap-0 items-stretch rounded-xl overflow-hidden"
            style={{
              background: 'var(--shell-surface)',
              border: '1px solid var(--shell-border)',
              boxShadow: '0 10px 30px rgba(0,0,0,0.04), 0 1px 0 rgba(255,255,255,0.6) inset',
            }}
            data-testid="pattern-flow-strip"
          >
            {/* STEP 1 */}
            <FlowStep
              n="01"
              icon={MousePointerClick}
              title="You choose the pattern"
              body="From 8 universal transaction primitives."
              tone="primary"
            />
            <FlowArrow />

            {/* STEP 2 */}
            <FlowStep
              n="02"
              icon={Cpu}
              title="Granullo makes it executable"
              body="Intent compiles into structural conditions and allocations."
              tone="accent"
            />
            <FlowArrow />

            {/* STEP 3 */}
            <FlowStep
              n="03"
              icon={Sparkles}
              title="Proof renders live"
              body="Released via the execution partner. A Twin is written. Memory compounds."
              tone="success"
            />
          </motion.div>
        </div>

        {/* ═══ PATTERN LIBRARY + SIMULATION ═══
             Pattern: Attio-grade left-list + right-panel.
             Grid has a bounded max-height so the right panel is always fully visible.
             The left list scrolls internally (the page doesn't scroll while hovering cards).
             Clicked card auto-centers itself inside the scrollable list. */}
        <div
          className="grid lg:grid-cols-[minmax(0,320px)_1fr] gap-6 lg:h-[680px]"
          data-testid="pattern-library-grid"
        >

          {/* LEFT — Pattern primitives (internally scrollable) */}
          <div
            className="flex flex-col gap-2 lg:min-h-0 lg:overflow-y-auto lg:pr-2 gb-scrollbar-subtle"
            data-testid="pattern-list"
          >
            <p className="text-[9px] font-mono uppercase tracking-[0.22em] mb-1.5 px-1" style={{ color: 'var(--shell-text-muted)' }}>
              Primitives
            </p>
            {PATTERNS.map((pat) => {
              const active = pat.id === activeId;
              const anySelected = activeId != null;
              const dim = !active && anySelected;
              const Ic = pat.icon;
              const handleCardClick = (e) => {
                setActiveId(pat.id);
                // Card sits in an internally-scrollable list. scrollIntoView with
                // block:'nearest' smoothly centers it without touching the page scroll.
                e.currentTarget.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
              };
              return (
                <motion.button
                  key={pat.id}
                  onClick={handleCardClick}
                  whileHover={{ y: -1, x: 1 }}
                  className="relative text-left px-4 py-3.5 rounded-lg cursor-pointer group overflow-hidden scroll-mt-28 shrink-0"
                  style={{
                    background: active ? `${SCARLET}07` : 'var(--shell-surface)',
                    border: `1px solid ${active ? `${SCARLET}55` : 'var(--shell-border)'}`,
                    opacity: dim ? 0.5 : 1,
                    transition: 'all 240ms ease',
                    boxShadow: active
                      ? `0 8px 24px ${SCARLET}1A, 0 0 0 1px ${SCARLET}18 inset`
                      : 'none',
                  }}
                  data-testid={`pattern-${pat.id}`}
                  data-lens-id={`pattern-${pat.id}`}
                  data-lens-type="rule-pack"
                  data-lens-title={pat.name}
                  data-lens-meaning={pat.synopsis}
                  data-lens-actions={(pat.proof || []).join('|')}
                  data-lens-state={pat.flagship ? 'flagship' : 'standard'}
                  data-lens-priority={pat.flagship ? 'high' : 'normal'}
                >
                  {active && (
                    <>
                      <span className="absolute top-0 bottom-0 left-0 w-[2px] rounded-l-lg" style={{ background: SCARLET }} />
                      <span
                        aria-hidden
                        className="absolute -right-6 -top-6 w-24 h-24 rounded-full pointer-events-none"
                        style={{ background: `radial-gradient(circle, ${SCARLET}18 0%, transparent 70%)` }}
                      />
                    </>
                  )}
                  {/* Flagship ribbon — surfaces the most-deployed pattern */}
                  {pat.flagship && (
                    <span
                      className="absolute top-2 right-2 inline-flex items-center gap-1 text-[8.5px] font-mono font-semibold uppercase tracking-[0.18em] px-1.5 py-[2px] rounded-sm"
                      style={{
                        color: '#fff',
                        background: SCARLET,
                        boxShadow: `0 2px 6px ${SCARLET}55`,
                        letterSpacing: '0.12em',
                      }}
                      data-testid={`pattern-flagship-${pat.id}`}
                    >
                      ★ Flagship
                    </span>
                  )}
                  <div className="flex items-start gap-3">
                    {/* Icon badge */}
                    <div
                      className="shrink-0 flex items-center justify-center rounded-lg transition-all duration-200"
                      style={{
                        width: 38,
                        height: 38,
                        background: active ? SCARLET : 'var(--shell-bg)',
                        border: `1px solid ${active ? SCARLET : 'var(--shell-border)'}`,
                        color: active ? '#fff' : 'var(--shell-text)',
                        boxShadow: active ? `0 6px 14px ${SCARLET}35` : 'none',
                      }}
                    >
                      <Ic size={17} strokeWidth={1.7} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-[9px] font-mono uppercase tracking-[0.18em] font-semibold"
                          style={{ color: active ? SCARLET : 'var(--shell-text-muted)' }}>
                          {pat.domain}
                        </span>
                        <ArrowRight size={12}
                          className={`transition-all duration-200 ${active ? 'opacity-100 translate-x-0' : 'opacity-0 group-hover:opacity-70 group-hover:translate-x-0.5'}`}
                          style={{ color: active ? SCARLET : 'var(--shell-text-muted)' }} />
                      </div>
                      <p className="text-[13px] font-semibold leading-tight" style={{ color: 'var(--shell-text)', letterSpacing: '-0.01em' }}>
                        {pat.name}
                      </p>
                      <p className="text-[11px] leading-[1.45] mt-1" style={{ color: 'var(--shell-text-secondary)' }}>
                        {pat.synopsis}
                      </p>
                      {/* Outcome microtag — previews what this pattern produces */}
                      {pat.tag && (
                        <span
                          className="inline-flex items-center gap-1 mt-2.5 text-[9.5px] font-mono font-semibold uppercase tracking-[0.16em] px-1.5 py-0.5 rounded"
                          style={{
                            color: active ? SCARLET : '#10B981',
                            background: active ? `${SCARLET}10` : 'rgba(16,185,129,0.08)',
                            border: `1px solid ${active ? `${SCARLET}40` : 'rgba(16,185,129,0.28)'}`,
                          }}
                        >
                          <span className="text-[11px] leading-none">{active ? '●' : '✓'}</span>
                          {pat.tag}
                        </span>
                      )}
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* RIGHT — Live simulation panel (always fully visible; list scrolls on its side) */}
          <div className="relative lg:min-h-0 lg:overflow-y-auto gb-scrollbar-subtle" data-testid="pattern-simulation-wrapper">
          <AnimatePresence mode="wait">
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}
              className="rounded-2xl overflow-hidden relative"
              style={{
                background: 'var(--shell-surface)',
                border: '1px solid var(--shell-border)',
                boxShadow: '0 20px 60px rgba(0,0,0,0.06), 0 1px 0 rgba(255,255,255,0.6) inset',
              }}
              data-testid="pattern-simulation"
            >
              {/* Header chrome */}
              <div className="flex items-center justify-between px-5 py-3.5" style={{ borderBottom: '1px solid var(--shell-border)' }}>
                <div className="flex items-center gap-3">
                  <div
                    className="shrink-0 flex items-center justify-center rounded-md"
                    style={{
                      width: 30, height: 30,
                      background: SCARLET,
                      color: '#fff',
                      boxShadow: `0 4px 10px ${SCARLET}35`,
                    }}
                  >
                    {(() => { const Ic = p.icon; return Ic ? <Ic size={15} strokeWidth={1.8} /> : null; })()}
                  </div>
                  <div className="flex flex-col leading-tight">
                    <span className="text-[9px] font-mono uppercase tracking-[0.2em] font-semibold" style={{ color: SCARLET }}>
                      {p.domain}
                    </span>
                    <span className="text-[13px] font-semibold" style={{ color: 'var(--shell-text)', letterSpacing: '-0.01em' }}>
                      {p.name}
                    </span>
                  </div>
                </div>
                <span className="text-[9px] font-mono uppercase tracking-[0.18em] inline-flex items-center gap-1.5"
                  style={{ color: EMERALD }}>
                  {p.flagship && (
                    <span
                      className="inline-flex items-center gap-1 text-[8.5px] font-mono font-semibold uppercase tracking-[0.18em] px-1.5 py-[2px] rounded-sm mr-2"
                      style={{
                        color: '#fff',
                        background: SCARLET,
                        boxShadow: `0 2px 6px ${SCARLET}55`,
                      }}
                      data-testid="simulation-flagship-badge"
                    >
                      ★ {p.flagshipNote || 'Flagship pattern'}
                    </span>
                  )}
                  <motion.span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: EMERALD, boxShadow: `0 0 6px ${EMERALD}` }}
                    animate={{ opacity: [1, 0.4, 1] }}
                    transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                  />
                  Live simulation
                </span>
              </div>

              <div className="p-5 sm:p-6 grid md:grid-cols-2 gap-6">
                {/* LEFT COLUMN — Intent → Conditions → Allocation */}
                <div className="flex flex-col gap-5">
                  <DoctrineBlock n="1" label="Intent" text={p.intent} />
                  <DoctrineBlock n="2" label="Conditions">
                    <div className="flex flex-col gap-1.5">
                      {p.conditions.map((c, i) => (
                        <span key={i} className="text-[11px] font-mono px-2.5 py-1.5 rounded"
                          style={{
                            background: 'var(--shell-bg)',
                            border: `1px solid var(--shell-border)`,
                            color: 'var(--shell-text)',
                          }}>
                          <span style={{ color: 'var(--shell-text-muted)' }}>{'//'}</span> {c}
                        </span>
                      ))}
                    </div>
                  </DoctrineBlock>
                  <DoctrineBlock n="3" label="Allocation">
                    <div className="flex flex-col gap-2">
                      {p.allocation.map((a, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="flex-1">
                            <div className="flex items-baseline justify-between mb-1">
                              <span className="text-[11.5px] font-medium" style={{ color: 'var(--shell-text)' }}>
                                {a.label}
                              </span>
                              <span className="text-[11px] font-mono tabular-nums font-semibold"
                                style={{ color: a.tone === 'reserved' ? SCARLET : 'var(--shell-text)' }}>
                                {a.amount}
                              </span>
                            </div>
                            <div className="relative h-1.5 rounded-full overflow-hidden"
                              style={{ background: 'var(--shell-border)' }}>
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${a.pct}%` }}
                                transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
                                className="absolute inset-y-0 left-0 rounded-full"
                                style={{
                                  background: a.tone === 'reserved' ? SCARLET : a.tone === 'primary' ? 'var(--shell-text)' : '#6B7280',
                                }}
                              />
                            </div>
                            {a.note && (
                              <span className="text-[9px] font-mono uppercase tracking-[0.14em] mt-1 inline-block"
                                style={{ color: SCARLET }}>
                                · {a.note}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </DoctrineBlock>
                </div>

                {/* RIGHT COLUMN — Proof → Release → Twin */}
                <div className="flex flex-col gap-5">
                  <DoctrineBlock n="4" label="Proof">
                    <ul className="flex flex-col gap-1.5">
                      {p.proof.map((pr, i) => (
                        <li key={i} className="flex gap-2 text-[12px] leading-[1.5]" style={{ color: 'var(--shell-text)' }}>
                          <CheckCircle2 size={12} style={{ color: EMERALD, marginTop: 4, flexShrink: 0 }} />
                          <span>{pr}</span>
                        </li>
                      ))}
                    </ul>
                  </DoctrineBlock>

                  <DoctrineBlock n="5" label="Release">
                    <div className="rounded-lg px-3.5 py-3"
                      style={{
                        background: `${EMERALD}08`,
                        border: `1px solid ${EMERALD}35`,
                      }}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <Lock size={12} style={{ color: EMERALD }} />
                        <span className="text-[10px] font-mono uppercase tracking-[0.18em] font-semibold" style={{ color: EMERALD }}>
                          Blocked before Release → Approved → Executed
                        </span>
                      </div>
                      <p className="text-[11.5px] leading-[1.5]" style={{ color: 'var(--shell-text)' }}>
                        {p.release}
                      </p>
                    </div>
                  </DoctrineBlock>

                  {/* Payment Twin preview */}
                  <div className="rounded-lg p-4 relative"
                    style={{
                      background: `${SCARLET}05`,
                      border: `1px solid ${SCARLET}30`,
                    }}
                    data-testid="pattern-twin-preview"
                  >
                    <p className="text-[9px] font-mono uppercase tracking-[0.2em] mb-2" style={{ color: SCARLET, fontWeight: 700 }}>
                      ✦ Payment Twin preview
                    </p>
                    <div className="grid grid-cols-1 gap-2 text-[11.5px] leading-[1.5]">
                      <TwinRow label="Future risk"    value={p.twinRisk}     color={SCARLET} />
                      <TwinRow label="Structural fix" value={p.twinFix}      color={'var(--shell-text)'} />
                      <TwinRow label="Outcome"        value={p.twinOutcome}  color={EMERALD} />
                    </div>
                  </div>

                  {/* ─────────── Run-lifecycle: real, transactional ─────────── */}
                  <LifecyclePanel
                    pattern={p}
                    state={lifecycleState}
                    result={lifecycleResult}
                    error={lifecycleError}
                    onRun={runLifecycle}
                  />
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
          </div>
        </div>

        {/* ═══ USED ACROSS ═══ */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.5 }}
          className="mt-12 pt-8"
          style={{ borderTop: '1px solid var(--shell-border)' }}
          data-testid="pattern-used-across"
        >
          <p className="text-[10px] font-mono uppercase tracking-[0.22em] mb-3" style={{ color: 'var(--shell-text-muted)' }}>
            Used across
          </p>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-[11.5px] font-mono uppercase tracking-[0.14em]"
            style={{ color: 'var(--shell-text)' }}>
            {USED_ACROSS.map((u, i) => (
              <React.Fragment key={u}>
                <span>{u}</span>
                {i < USED_ACROSS.length - 1 && <span style={{ color: 'var(--shell-text-muted)' }}>·</span>}
              </React.Fragment>
            ))}
          </div>
        </motion.div>

        {/* ═══ FOUNDER LINES ═══ */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.6 }}
          className="mt-14 max-w-[900px]"
          data-testid="pattern-founder-lines"
        >
          <div className="relative pl-5" style={{ borderLeft: `2px solid ${SCARLET}` }}>
            <p className="text-[18px] sm:text-[22px] leading-[1.4] mb-3"
              style={{
                fontFamily: '"Source Serif 4", Georgia, serif',
                color: 'var(--shell-text)',
                letterSpacing: '-0.015em',
              }}>
              We do not help you calculate obligations later. We make it{' '}
              <span style={{ color: SCARLET, fontWeight: 600 }}>structurally impossible</span> to spend them incorrectly in the first place.
            </p>
            <p className="text-[14px] sm:text-[16px] leading-[1.55]"
              style={{ color: 'var(--shell-text-secondary)', fontStyle: 'italic' }}>
              Granullo should never be a feature inside someone else&rsquo;s payment stack.
              It must be the reason their payment stack becomes trusted.
            </p>
            <p className="text-[10px] font-mono uppercase tracking-[0.22em] mt-4" style={{ color: 'var(--shell-text-muted)' }}>
              — Founder doctrine
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Subcomponents ─── */
function DoctrineBlock({ n, label, text, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-sm text-[10px] font-mono font-bold"
          style={{ background: `${SCARLET}10`, border: `1px solid ${SCARLET}40`, color: SCARLET }}>
          {n}
        </span>
        <span className="text-[10px] font-mono uppercase tracking-[0.2em] font-semibold" style={{ color: 'var(--shell-text)' }}>
          {label}
        </span>
      </div>
      {text ? (
        <p className="text-[12.5px] leading-[1.6]" style={{ color: 'var(--shell-text)' }}>{text}</p>
      ) : children}
    </div>
  );
}

function TwinRow({ label, value, color }) {
  return (
    <div className="grid grid-cols-[112px_1fr] gap-2 items-baseline">
      <span className="text-[9px] font-mono uppercase tracking-[0.16em]" style={{ color: 'var(--shell-text-muted)' }}>
        {label}
      </span>
      <span style={{ color }}>{value}</span>
    </div>
  );
}

/* ─── 3-step flow helpers ─── */
function FlowStep({ n, icon: Ic, title, body, tone }) {
  const toneColor = tone === 'accent' ? SCARLET : tone === 'success' ? EMERALD : 'var(--shell-text)';
  const toneBg    = tone === 'accent' ? `${SCARLET}0A` : tone === 'success' ? `${EMERALD}0A` : 'transparent';
  return (
    <div className="flex gap-4 px-5 py-5 min-h-[112px]" style={{ background: toneBg }}>
      {/* Number + icon stack */}
      <div className="flex flex-col items-start gap-2 shrink-0">
        <span className="text-[10px] font-mono uppercase tracking-[0.2em] font-bold" style={{ color: toneColor }}>
          {n}
        </span>
        <div
          className="flex items-center justify-center rounded-lg"
          style={{
            width: 40, height: 40,
            background: tone === 'primary' ? 'var(--shell-bg)' : toneColor,
            color: tone === 'primary' ? 'var(--shell-text)' : '#fff',
            border: `1px solid ${tone === 'primary' ? 'var(--shell-border)' : toneColor}`,
            boxShadow: tone !== 'primary' ? `0 6px 14px ${toneColor}30` : 'none',
          }}
        >
          <Ic size={18} strokeWidth={1.8} />
        </div>
      </div>
      <div className="flex-1 min-w-0 pt-0.5">
        <p className="text-[14px] font-semibold leading-tight mb-1" style={{ color: 'var(--shell-text)', letterSpacing: '-0.01em' }}>
          {title}
        </p>
        <p className="text-[12px] leading-[1.5]" style={{ color: 'var(--shell-text-secondary)' }}>
          {body}
        </p>
      </div>
    </div>
  );
}

function FlowArrow() {
  return (
    <div className="hidden md:flex items-center justify-center px-2"
      style={{ color: 'var(--shell-text-muted)' }}>
      <motion.span
        animate={{ x: [0, 3, 0] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
        className="text-[14px]"
      >
        →
      </motion.span>
    </div>
  );
}


/* ─── Lifecycle panel — real, transactional Intent → Release ─── */
function LifecyclePanel({ pattern, state, result, error, onRun }) {
  const isRunning = state === 'running';
  const isDone    = state === 'done' && !!result;
  const isError   = state === 'error';

  return (
    <div className="rounded-lg p-4"
      style={{
        background: isDone
          ? `${EMERALD}06`
          : isError
            ? `${SCARLET}06`
            : 'var(--shell-surface)',
        border: `1px solid ${isDone ? `${EMERALD}40` : isError ? `${SCARLET}40` : 'var(--shell-border)'}`,
      }}
      data-testid="pattern-lifecycle-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-[9px] font-mono uppercase tracking-[0.2em]"
           style={{ color: isDone ? EMERALD : 'var(--shell-text)', fontWeight: 700 }}>
          ▷ Run lifecycle · live transaction
        </p>
        <button
          type="button"
          onClick={onRun}
          disabled={isRunning}
          className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.16em] font-semibold px-2.5 py-1.5 rounded transition-all"
          style={{
            background: isRunning ? 'var(--shell-border)' : SCARLET,
            color: isRunning ? 'var(--shell-text-muted)' : '#fff',
            cursor: isRunning ? 'wait' : 'pointer',
            opacity: isRunning ? 0.7 : 1,
            boxShadow: isRunning ? 'none' : `0 3px 10px ${SCARLET}40`,
          }}
          data-testid="pattern-run-lifecycle-btn"
        >
          {isRunning
            ? <>
                <Loader2 size={11} className="animate-spin" />
                Running…
              </>
            : isDone
              ? <>
                  <FileCheck2 size={11} />
                  Run again
                </>
              : <>
                  <Sparkles size={11} />
                  Execute
                </>
          }
        </button>
      </div>

      {state === 'idle' && (
        <p className="text-[11.5px] leading-[1.55]" style={{ color: 'var(--shell-text-secondary)' }}>
          This is not a mock-up. Hit <span style={{ color: SCARLET, fontWeight: 600 }}>Execute</span> to run
          the {pattern.name} pattern through the deterministic Intent → Conditions → Allocation → Proof → Release engine
          on the live backend. The release seals a Payment Twin in MongoDB. Zero LLM. Pure arithmetic + SHA-256.
        </p>
      )}

      {isError && (
        <div className="flex items-start gap-2 text-[11.5px]" style={{ color: SCARLET }}>
          <AlertCircle size={14} style={{ marginTop: 2, flexShrink: 0 }} />
          <span>Lifecycle blocked — {error}</span>
        </div>
      )}

      {isDone && (
        <div className="flex flex-col gap-3" data-testid="pattern-lifecycle-result">
          {/* Step rail */}
          <div className="flex flex-col gap-1">
            {result.steps.map((s, i) => {
              const ok = s.status === 'ok';
              const stepColor = ok ? EMERALD : SCARLET;
              return (
                <div key={i} className="grid grid-cols-[14px_140px_1fr] gap-2 items-baseline text-[10.5px] font-mono"
                  data-testid={`lifecycle-step-${s.step}`}>
                  <span style={{ color: stepColor }}>{ok ? '✓' : '✗'}</span>
                  <span className="uppercase tracking-[0.14em]" style={{ color: 'var(--shell-text)' }}>
                    {s.step.replace(/_/g, ' ')}
                  </span>
                  <span className="truncate" style={{ color: 'var(--shell-text-muted)' }}>
                    {Object.entries(s.detail || {}).slice(0, 2)
                      .map(([k, v]) => `${k}=${typeof v === 'string' ? v.slice(0, 18) : v}`)
                      .join(' · ') || new Date(s.ts).toLocaleTimeString()}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Twin receipt */}
          {result.twin && (
            <div className="rounded-md px-3 py-2.5 text-[10.5px] font-mono leading-[1.5]"
              style={{
                background: 'var(--shell-bg)',
                border: `1px solid ${EMERALD}50`,
                color: 'var(--shell-text)',
              }}
              data-testid="pattern-lifecycle-twin"
            >
              <div className="flex items-center gap-1.5 mb-1.5"
                style={{ color: EMERALD }}>
                <Lock size={11} />
                <span className="uppercase tracking-[0.16em] font-semibold text-[9.5px]">Twin sealed · MongoDB</span>
              </div>
              <div style={{ color: 'var(--shell-text-muted)' }}>
                <span style={{ color: 'var(--shell-text)' }}>twin_id</span> {result.twin.twin_id}
              </div>
              <div style={{ color: 'var(--shell-text-muted)' }}>
                <span style={{ color: 'var(--shell-text)' }}>evidence</span> {result.twin.evidence_hash}
              </div>
              <div style={{ color: 'var(--shell-text-muted)' }}>
                <span style={{ color: 'var(--shell-text)' }}>allocation</span> {result.twin.allocation_total_pct}% · proof_count={result.twin.proof_count}
              </div>
            </div>
          )}

          {result.state === 'blocked' && (
            <div className="text-[11px] leading-[1.5]" style={{ color: SCARLET }}>
              Release was structurally blocked — money did not move. This is the doctrine working.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
